import type { FC } from 'react'
import { Dialog, DialogContent } from '@mui/material'
import { Form, Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import { DialogTitleClosable } from '@dhl/components'
import { useCreateUser, useUpdateOrganisationForUser } from '@dhl/mdp-api'
import { Box, Button, Divider, Grid, H5 } from '@dhl-ui/components'
import { TextFieldInput } from '@dhl/formik'
import { useNotificationMessage } from '~/common'
import { getErrorMessage } from '~/common/util/axiosUtil'
import { type BaseUser, createBaseUserValidationSchema, initialBaseUser } from '~/modules/users/model'

export interface AddUserProps {
  open: boolean
  onClose?: () => void
  organisationId: string
  onRefreshData: (value: boolean) => void
}

export const AddUserForm: FC<AddUserProps> = ({ open, onClose, organisationId, onRefreshData }: AddUserProps) => {
  const { t } = useTranslation()
  const { showError, showSuccess } = useNotificationMessage()
  const validationSchema = createBaseUserValidationSchema(t)
  const { mutateAsync: createUser } = useCreateUser()
  const { mutateAsync: updateOrganisationUser } = useUpdateOrganisationForUser()

  const connectUserToOrganisation = (userId: string) => {
    updateOrganisationUser(
      { emailOrId: userId, organisationId },
      {
        onSuccess: () => {
          onRefreshData(true)
        },
        onError: (error) => {
          showError(getErrorMessage(error, t('admin.organisationsPage.userForm.errorMessage')))
        },
      }
    )
  }

  const onSubmit = (baseUser: BaseUser) => {
    createUser(
      { data: baseUser },
      {
        onSuccess: (addedUser) => {
          if (addedUser) {
            connectUserToOrganisation(addedUser.id)
            showSuccess(t('admin.organisationsPage.userForm.addedUserMessage'))
            onClose && onClose()
          } else {
            showError(getErrorMessage(new Error('not_found'), t('admin.organisationsPage.userForm.errorMessage')))
          }
        },
        onError: (error) => {
          showError(getErrorMessage(error, t('admin.organisationsPage.userForm.errorMessage')))
        },
      }
    )
  }

  return (
    <Dialog open={open} maxWidth="sm" data-testid="add-user-dialog" fullWidth>
      <DialogTitleClosable onClose={onClose}>
        <Box alignSelf="left">
          <H5>{t('admin.organisationsPage.userForm.title')}</H5>
        </Box>
      </DialogTitleClosable>

      <Divider />
      <DialogContent>
        <Box mb={2} mt={2}>
          <H5>{t('admin.organisationsPage.userForm.account')}</H5>
        </Box>
        <Box mb={2}>
          <Formik
            initialValues={initialBaseUser}
            onSubmit={onSubmit}
            validateOnMount
            validationSchema={validationSchema}
          >
            {({ isSubmitting }) => {
              return (
                <Form data-testid="user-form">
                  <Box>
                    <Grid container spacing={2} direction="column">
                      <Grid container>
                        <Grid size={{ sm: 6 }}>
                          <TextFieldInput
                            fullWidth
                            label={t('admin.organisationsPage.userForm.name')}
                            name="name.firstName"
                            type="text"
                          />
                        </Grid>
                        <Grid size={{ sm: 6 }}>
                          <TextFieldInput
                            fullWidth
                            label={t('admin.organisationsPage.userForm.surname')}
                            name="name.lastName"
                            type="text"
                          />
                        </Grid>
                      </Grid>
                      <Grid>
                        <TextFieldInput
                          fullWidth
                          label={t('admin.organisationsPage.userForm.email')}
                          name="email.address"
                          type="text"
                        />
                      </Grid>
                      <Grid display="flex" justifyContent="flex-end" size={{ sm: 12 }} gap={1}>
                        <Button type="button" onClick={onClose} color="primary">
                          {t('admin.organisationsPage.userForm.cancelButton')}
                        </Button>
                        <Button type="submit" variant="contained" color="primary" size="medium" disabled={isSubmitting}>
                          {t('admin.organisationsPage.userForm.addButton')}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </Form>
              )
            }}
          </Formik>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
